import { Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  CheckCircle,
  Circle,
  Clock,
  Home,
  Package,
  PackageCheck,
  Truck,
} from "lucide-react";
import Loader from "./Loader";
import FooterAdmin from "./Footer/FooterAdmin";
import "./index.css";
import {
  cancelledSteps,
  lm_status_message,
  lm_status_track,
} from "./utils/constant";
import { getDeliveryDate, getOrderActions } from "./utils/functions";
import toast from "react-hot-toast";
import { getOrderTrackingData } from "../Services/orders";
import Header from "./header";

const OrderTrackingInfo = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { orderNumber } = params || {};

  const [orderDetail, setOrderDetail] = useState({});
  const [orderNo, setOrderNo] = useState("");
  const order_Status = orderDetail?.order?.order_status || null;
  const lm_Status = orderDetail?.order?.lm_status || null;

  const fetchOrderTrackingData = async (orderNumberToFetch) => {
    if (!orderNumberToFetch) {
      toast.error("Enter a valid order number");
      return;
    }
    const response = await getOrderTrackingData({
      order_number: orderNumberToFetch,
    });
    const { data, status } = response || {};
    if (status === 200 || status === 201) {
      setOrderDetail(data?.data);
      navigate(`/orders/tracking/${orderNumberToFetch}`);
    } else if (status === 404) {
      toast.error(data?.message);
      setOrderNo("");
      navigate(`/orders/tracking`);
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    if (orderNumber) {
      fetchOrderTrackingData(orderNumber);
    }
  }, [orderNumber]);

  const handleButtonClick = () => {
    fetchOrderTrackingData(orderNo);
  };

  const trackingSteps = [
    {
      id: "confirmed",
      label: "Confirmed",
      icon: <CheckCircle className="w-5 h-5" />,
      completed: false,
      current: false,
    },
    {
      id: "allocated",
      label: "Allocated",
      icon: <Package className="w-5 h-5" />,
      completed: false,
      current: false,
    },
    {
      id: "packed",
      label: "Ready To Ship",
      icon: <PackageCheck className="w-5 h-5" />,
      completed: false,
      current: false,
    },
    {
      id: "shipped complete",
      label: "Dispatched",
      icon: <Truck className="w-5 h-5" />,
      completed: false,
      current: false,
    },
    {
      id: "intransit",
      label:
        orderDetail?.order?.order_status === "Intransit" &&
        orderDetail?.order?.lm_status
          ? orderDetail.order?.lm_status?.charAt(0)?.toUpperCase() +
            orderDetail?.order?.lm_status?.slice(1)
          : "In Transit",
      icon: <Clock className="w-5 h-5" />,
      completed: false,
      current: false,
    },
    {
      id: "out for delivery",
      label: "Out For Delivery",
      icon: <Truck className="w-5 h-5" />,
      completed: false,
      current: false,
    },
    {
      id: "delivered",
      label: "Delivered",
      icon: <Home className="w-5 h-5" />,
      completed: false,
      current: false,
    },
  ];

  const isCancelled =
    orderDetail?.order?.order_status?.toLowerCase() === "cancelled";
  const isRefundExist = orderDetail?.refundDetail ? true : false;

  const [TrackingSteps, setTrackingSteps] = useState([]);

  useEffect(() => {
    const currentOrderStatus = orderDetail?.order?.order_status?.toLowerCase();
    if (currentOrderStatus && !isCancelled && !isRefundExist) {
      let cloneTrackingSteps = [...trackingSteps];
      const orderTrackingStatus = cloneTrackingSteps.map((item) => item.id);

      if (lm_status_track[lm_Status] === "Refund initiated") {
        cloneTrackingSteps = [
          {
            id: "initiated",
            label: "Refund Initiated",
            icon: <Clock className="w-5 h-5" />,
            completed: true,
            current: true,
          },
          {
            id: "Processed",
            label: "Refund Approved",
            icon: <CheckCircle className="w-5 h-5" />,
            completed: false,
            current: false,
          },
        ];
      } else if (orderTrackingStatus.includes(currentOrderStatus)) {
        for (let index = 0; index < cloneTrackingSteps.length; index++) {
          const element = cloneTrackingSteps[index];
          if (element.id === currentOrderStatus) {
            cloneTrackingSteps[index] = {
              ...cloneTrackingSteps[index],
              completed: true,
              current: true,
            };
            break;
          } else {
            cloneTrackingSteps[index] = {
              ...cloneTrackingSteps[index],
              completed: true,
              current: false,
            };
          }
        }
      }

      setTrackingSteps(cloneTrackingSteps);
    } else {
      if (isRefundExist) {
        const cloneCancelledSteps = [...cancelledSteps];
        for (let index = 0; index < cloneCancelledSteps.length; index++) {
          const element = cloneCancelledSteps[index];
          if (element.id === currentOrderStatus) {
            cloneCancelledSteps[index] = {
              ...cloneCancelledSteps[index],
              completed: true,
              current: true,
            };
            break;
          } else {
            cloneCancelledSteps[index] = {
              ...cloneCancelledSteps[index],
              completed: true,
              current: false,
            };
          }
        }
        setTrackingSteps(cloneCancelledSteps);
      }
    }
  }, [orderNumber, orderDetail]);

  const order_status =
    order_Status === "Intransit" && lm_Status
      ? lm_status_message[lm_Status] || lm_Status
      : lm_status_message[order_Status] || order_Status;

  return (
    <Suspense fallback={<Loader />}>
      {!orderNumber ? (
        <>
          <div className="tracking-page">
            {/* Navigation */}
            <Header />
            <hr className="divider" />

            {/* Tracking Section */}
            <div className="tracking-section">
              <div className="tracking-box">
                <h1 className="tracking-title">Track Your Order</h1>

                <input
                  type="text"
                  className="tracking-input"
                  onChange={(e) => setOrderNo(e.target.value)}
                  placeholder="Enter Order ID (Example: 1001)"
                />

                <button className="tracking-btn" onClick={handleButtonClick}>
                  Get Tracking Data
                </button>
              </div>
            </div>

            <FooterAdmin />
          </div>
        </>
      ) : (
        <div className="delivery-tracking-wrapper">
          <Header />
          <hr className="divider" />

          <div className="tracking-section">
            <div className="tracking-container">
              <h3 className="tracking-heading">Your Order Tracking Status</h3>

              {/* Order Info Boxes */}
              <div className="order-info-grid">
                {/* Order Number */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem", // space-x-3
                    padding: "0.75rem", // p-3
                    backgroundColor: "#f3f4f6", // bg-gray-100
                    borderRadius: "0.5rem", // rounded-lg
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      height: "1.5rem", // h-6
                      width: "1.5rem", // w-6
                      color: "#3b82f6", // text-blue-500
                    }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5H21m-4 13a2 2 0 11-4 0m-4 0a2 2 0 11-4 0m.8-10H16m.2 12H7.8"
                    />
                  </svg>

                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div
                      style={{
                        fontSize: "medium",
                        fontWeight: 600,
                        color: "#374151" /* text-gray-700 */,
                      }}
                    >
                      Order No :
                    </div>
                    <div
                      style={{
                        fontSize: "larger",
                        color: "#6b7280" /* text-gray-500 */,
                      }}
                    >
                      #{orderNumber || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Order Status */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "0.5rem",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      height: "1.5rem",
                      width: "1.5rem",
                      color: "#22c55e", // text-green-500
                    }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0h2m-2 0a2 2 0 01-2-2v-3a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2m0 0h2a2 2 0 002-2v-3a2 2 0 00-2-2m-3-4h6a2 2 0 002-2v-3a2 2 0 00-2-2h-6a2 2 0 00-2 2v3a2 2 0 002 2z"
                    />
                  </svg>

                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div
                      style={{
                        fontSize: "medium",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Order Status :
                    </div>
                    <div
                      style={{
                        fontSize: "larger",
                        color: "#6b7280",
                      }}
                    >
                      {order_status}
                    </div>
                  </div>
                </div>

                {/* Expected Delivery */}
                {lm_status_track[lm_Status] !== "Refund initiated" &&
                  getOrderActions(orderDetail?.order?.order_status)
                    .showEstimate && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem", // space-x-3
                        padding: "0.75rem", // p-3
                        backgroundColor: "#f3f4f6", // bg-gray-100
                        borderRadius: "0.5rem", // rounded-lg
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          height: "1.5rem", // h-6
                          width: "1.5rem", // w-6
                          color: "#a855f7", // text-blue-500
                        }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5H21m-4 13a2 2 0 11-4 0m-4 0a2 2 0 11-4 0m.8-10H16m.2 12H7.8"
                        />
                      </svg>

                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <div
                          style={{
                            fontSize: "medium",
                            fontWeight: 600,
                            color: "#374151" /* text-gray-700 */,
                          }}
                        >
                          Expected Delivery :
                        </div>
                        <div
                          style={{
                            fontSize: "larger",
                            color: "#6b7280" /* text-gray-500 */,
                          }}
                        >
                          {getDeliveryDate(
                            orderDetail?.order?.cust_delivery_date,
                            orderDetail?.order?.revised_delivery_date
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Delivered Date */}
                {orderDetail?.order?.delivery_date &&
                  getOrderActions(orderDetail?.order?.order_status)
                    .showDeliveredDate &&
                  orderDetail?.order?.order_status !== "cancelled" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem", // space-x-3
                        padding: "0.75rem", // p-3
                        backgroundColor: "#f3f4f6", // bg-gray-100
                        borderRadius: "0.5rem", // rounded-lg
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          height: "1.5rem", // h-6
                          width: "1.5rem", // w-6
                          color: "#a855f7", // text-blue-500
                        }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5H21m-4 13a2 2 0 11-4 0m-4 0a2 2 0 11-4 0m.8-10H16m.2 12H7.8"
                        />
                      </svg>

                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <div
                          style={{
                            fontSize: "medium",
                            fontWeight: 600,

                            color: "#374151" /* text-gray-700 */,
                          }}
                        >
                          Delivered Delivery :
                        </div>
                        <div
                          style={{
                            fontSize: "larger",
                            color: "#6b7280" /* text-gray-500 */,
                          }}
                        >
                          {moment(orderDetail?.order?.delivery_date).format(
                            "YYYY-MM-DD"
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* AWB */}
                {orderDetail?.order?.awb_no && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem", // space-x-3
                      padding: "0.75rem", // p-3
                      backgroundColor: "#f3f4f6", // bg-gray-100
                      borderRadius: "0.5rem", // rounded-lg
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        height: "1.5rem", // h-6
                        width: "1.5rem", // w-6
                        color: "#a855f7", // text-blue-500
                      }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5H21m-4 13a2 2 0 11-4 0m-4 0a2 2 0 11-4 0m.8-10H16m.2 12H7.8"
                      />
                    </svg>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div
                        style={{
                          fontSize: "medium",
                          fontWeight: 600,
                          color: "#374151" /* text-gray-700 */,
                        }}
                      >
                        AWB No :
                      </div>
                      <div
                        style={{
                          fontSize: "larger",
                          color: "#6b7280" /* text-gray-500 */,
                        }}
                      >
                        {orderDetail?.order?.awb_no}
                      </div>
                    </div>
                  </div>
                )}

                {/* Delivery Partner */}
                {orderDetail?.order?.lm_partner && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem", // space-x-3
                      padding: "0.75rem", // p-3
                      backgroundColor: "#f3f4f6", // bg-gray-100
                      borderRadius: "0.5rem", // rounded-lg
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        height: "1.5rem", // h-6
                        width: "1.5rem", // w-6
                        color: "#a855f7", // text-blue-500
                      }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5H21m-4 13a2 2 0 11-4 0m-4 0a2 2 0 11-4 0m.8-10H16m.2 12H7.8"
                      />
                    </svg>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div
                        style={{
                          fontSize: "medium",
                          fontWeight: 600,
                          color: "#374151" /* text-gray-700 */,
                        }}
                      >
                        Delivery Partner :
                      </div>
                      <div
                        style={{
                          fontSize: "larger",
                          color: "#6b7280" /* text-gray-500 */,
                        }}
                      >
                        {orderDetail?.order?.lm_partner}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* OLD */}
              <div className="desktop-progress">
                {TrackingSteps.length > 0 && (
                  <div className="tracking-progress">
                    <div className="progress-bar-box">
                      <div className="progress-bar-container">
                        <div className="progress-track"></div>
                        <div
                          className="progress-fill"
                          style={{
                            width: `${
                              ((TrackingSteps.filter((s) => s.completed)
                                .length -
                                1) /
                                (TrackingSteps.length - 1)) *
                              100
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="progress-steps">
                      {TrackingSteps.map((step) => (
                        <div key={step.id} className="step">
                          <div
                            className={`step-circle ${
                              step.completed
                                ? step.current
                                  ? "current"
                                  : "done"
                                : "pending"
                            }`}
                          >
                            {step.completed ? (
                              step.icon
                            ) : (
                              <Circle className="circle-icon" />
                            )}
                          </div>
                          <p
                            className={`step-label ${
                              step.current
                                ? "active"
                                : step.completed
                                ? "done"
                                : ""
                            }`}
                          >
                            {step.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Tracking Progress */}
              {TrackingSteps.length > 0 && (
                <div className="tracking-container">
                  <div className="tracking-inner">
                    {/* Mobile Progress */}
                    <div className="mobile-progress">
                      {TrackingSteps.map((step, index) => (
                        <div key={step.id} className="mobile-step">
                          <div
                            className={`circle ${
                              step.completed ? "completed" : "incomplete"
                            }`}
                          >
                            {step.completed ? step.icon : <Circle />}
                          </div>
                          <div className="label">
                            <h3
                              className={`${
                                step.completed ? "completed" : "incomplete"
                              } ${step.current ? "current" : ""}`}
                            >
                              {step.label}
                            </h3>
                            {index < TrackingSteps.length - 1 && (
                              <div
                                className={`connector ${
                                  step.completed ? "completed" : "incomplete"
                                }`}
                              ></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <FooterAdmin />
        </div>
      )}
    </Suspense>
  );
};

export default OrderTrackingInfo;
